import { FlatList, Platform } from "react-native-web";
import { useEffect, useRef, useState } from "react";
import { AnimatePresence, Button, Group, Image, Theme, View, XStack, styled, useMedia } from "tamagui";
import { MarkdownComponent } from "../../../UtilComponents/Markdown.mjs";
import { SimpleText } from "../texts/index.mjs";
import "./html.css";
import "./github-markdown-light.css";
import { Flag, ThumbsDown, ThumbsUp } from "@tamagui/lucide-icons-2";
import { MiscUtils } from "@my/util";
import { jsx, jsxs } from "react/jsx-runtime";
const List = styled(FlatList, {
  bg: "$background",
  gap: "$3"
});
const avatars = ["https://images.unsplash.com/photo-1588798204072-e5f8e649d269?&w=100", "https://images.unsplash.com/photo-1736754079614-8b43bcba9926?&w=100"];
const getMessages = () => Array.from({
  length: 50
}).fill(0).map((_, i) => ({
  message: randSentence(),
  time: randRecentDate().toLocaleTimeString(),
  itsMe: i % 2 === 0,
  avatar: avatars[i % 2]
}));
function ChatList() {
  const [messages, setMessages] = useState([]);
  const listRef = useRef(null);
  useEffect(() => {
    setMessages(getMessages());
  }, []);
  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollToEnd({
        animated: true
      });
    }
  }, [messages]);
  return /* @__PURE__ */jsx(View, {
    flexDirection: "column",
    height: 700,
    children: /* @__PURE__ */jsx(List, {
      ref: listRef,
      inverted: true,
      data: messages,
      renderItem: ({
        item
      }) => /* @__PURE__ */jsx(ChatItem, {
        item
      }),
      keyExtractor: (item, index) => item.time.toString() + index,
      windowSize: 2
    })
  });
}
function ChatItem({
  item,
  actionFns,
  index
}) {
  const media = useMedia();
  const {
    message,
    time,
    avatar,
    itsMe,
    isInitialMessage
  } = item;
  const [showMessage, setShowMessage] = useState(true);
  const markdownBase = /* @__PURE__ */jsx(MarkdownComponent, {
    children: message
  });
  const markdownComponent = Platform.select({
    web: /* @__PURE__ */jsx("div", {
      className: "markdown-body",
      children: markdownBase
    }),
    native: markdownBase
  });
  const containerProps = media["max-sm"] ? {
    flexDirection: "colunm",
    alignItems: itsMe ? "flex-end" : "flex-start",
    alignSelf: itsMe ? "flex-end" : "flex-start"
  } : {
    flexDirection: itsMe ? "row-reverse" : "row",
    alignItems: "flex-start",
    alignSelf: itsMe ? "flex-end" : "flex-start",
    gap: "$4"
  };
  return /* @__PURE__ */jsx(AnimatePresence, {
    children: showMessage && /* @__PURE__ */jsxs(View, {
      ...containerProps,
      maxWidth: "100%",
      minWidth: "100%",
      marginVertical: media["max-sm"] ? 0 : 10,
      children: [/* @__PURE__ */jsx(View, {
        justifyContent: "center",
        alignItems: "center",
        marginTop: 5,
        width: 44,
        height: 44,
        bottom: 10,
        borderWidth: 1,
        borderRadius: 100,
        borderColor: "#85C19D",
        children: /* @__PURE__ */jsx(Image, {
          style: {
            width: itsMe ? "100%" : 28,
            height: itsMe ? "100%" : 24,
            borderRadius: itsMe ? 100 : 0
          },
          src: avatar
        })
      }), /* @__PURE__ */jsxs(View, {
        flexDirection: "column",
        alignItems: itsMe ? "flex-end" : "flex-start",
        width: "100%",
        justifyContent: "center",
        flexShrink: 1,
        children: [/* @__PURE__ */jsx(Theme, {
          name: itsMe ? "#E9ECEF" : "gray",
          children: /* @__PURE__ */jsx(View, {
            backgroundColor: !itsMe ? "#fff" : "rgba(240, 241, 244, .75)",
            padding: "$4",
            paddingVertical: "$3",
            borderRadius: 9,
            flexShrink: 1,
            borderColor: !itsMe ? "#ddd" : "rgb(224, 225, 230)",
            borderWidth: 1,
            maxWidth: "100%",
            children: markdownComponent
          })
        }), /* @__PURE__ */jsxs(XStack, {
          gap: 20,
          alignItems: "center",
          style: {
            width: "100%"
          },
          children: [/* @__PURE__ */jsx(SimpleText, {
            color: "#555555",
            fontSize: 10,
            fontWeight: "600",
            lineHeight: 13,
            mt: 8,
            ...(itsMe ? {
              marginLeft: "auto"
            } : {}),
            children: new Date(time).toLocaleTimeString("en-US", {
              hour: "2-digit",
              minute: "2-digit",
              hour12: true
            })
          }), !itsMe && !isInitialMessage ? /* @__PURE__ */jsx(XStack, {
            children: /* @__PURE__ */jsxs(Group, {
              size: "$1",
              orientation: "horizontal",
              gap: 1,
              marginLeft: "auto",
              marginTop: 0,
              borderRadius: 9,
              children: [/* @__PURE__ */jsx(Group.Item, {
                children: /* @__PURE__ */jsx(Button, {
                  icon: /* @__PURE__ */jsx(ThumbsUp, {}),
                  ...{
                    color: "black",
                    hoverStyle: {
                      backgroundColor: "rgba(133, 193, 157, 0.25)"
                    }
                  },
                  ...(item.rating === "helpful" ? {
                    backgroundColor: "rgba(133, 193, 157, 0.85)"
                  } : {}),
                  onPress: () => actionFns?.helpfulnessFn(item, "helpful"),
                  size: "$3",
                  children: media["max-sm"] ? "" : "Helpful"
                })
              }), /* @__PURE__ */jsx(Group.Item, {
                children: /* @__PURE__ */jsx(Button, {
                  icon: /* @__PURE__ */jsx(ThumbsDown, {}),
                  ...{
                    color: "black",
                    hoverStyle: {
                      backgroundColor: "rgba(251, 213, 61, 0.25)"
                    }
                  },
                  ...(item.rating === "not-helpful" ? {
                    backgroundColor: "rgba(251, 213, 61, 0.61)"
                  } : {}),
                  onPress: () => actionFns?.helpfulnessFn(item, "not-helpful"),
                  size: "$3",
                  children: media["max-sm"] ? "" : "Not Helpful"
                })
              }), /* @__PURE__ */jsx(Group.Item, {
                children: /* @__PURE__ */jsx(Button, {
                  icon: /* @__PURE__ */jsx(Flag, {}),
                  ...{
                    color: "black",
                    hoverStyle: {
                      backgroundColor: "#ff333342"
                    }
                  },
                  ...(item.issueReported ? {
                    backgroundColor: "#ff33338a"
                  } : {}),
                  onPress: async () => (await MiscUtils.dialog.confirm("Is there an issue with this response?")) ? actionFns?.reportFn(item, "There is an issue with this response.") : void 0,
                  size: "$3",
                  children: media["max-sm"] ? "" : "Report Issue"
                })
              })]
            })
          }) : null]
        })]
      })]
    })
  });
}
const ChatListControlled = ({
  messages,
  actionFns
}) => {
  const listRef = useRef(null);
  const media = useMedia();
  useEffect(() => {
    if (listRef?.current && messages.length > 0) {}
  }, [listRef, listRef.current]);
  useEffect(() => {
    if (listRef.current && messages.length > 0) {
      const timeoutFn = Platform.select({
        web: () => setTimeout(() => {
          [...listRef?.current?.["_listRef"]._scrollRef.firstChild.children][0].scrollIntoView({
            block: "end"
          });
        }, 100),
        native: () => {}
      });
      timeoutFn();
    }
  }, [messages, messages.length]);
  const getItemLayout = (_, index) => ({
    length: 300,
    // Estimated row height
    offset: 80 * index,
    index
  });
  return (
    //<YStack flex={1} width={'100%'}>
    /* @__PURE__ */
    jsx(List, {
      ref: listRef,
      "$group-window-gtXs": {
        p: "$8"
      },
      inverted: true,
      data: [...messages],
      renderItem: ({
        item,
        index
      }) => /* @__PURE__ */jsx(ChatItem, {
        item,
        actionFns,
        index
      }),
      keyExtractor: (item, index) => item.msgId,
      windowSize: 10,
      backgroundColor: "transparent",
      flexDirection: "column",
      height: 300,
      flex: 1,
      width: "100%",
      "$group-window-sm": {
        p: "$4"
      },
      contentContainerStyle: {
        gap: media["max-sm"] ? 4 : 16,
        flexGrow: 1,
        justifyContent: "flex-end",
        paddingBottom: 5
      },
      showsVerticalScrollIndicator: false
    })
  );
};
export { ChatList, ChatListControlled };
//# sourceMappingURL=ChatList.mjs.map
